// todo add actions button and actions menu and view details and reject modals

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  ClickAwayListener
} from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Search } from '@mui/icons-material';

import {
  Eye,
  Trash2,
  X,
  RefreshCw,
  GraduationCap,
  Layers,
  Users2,
  SlidersHorizontal,
  Check,
  SlidersHorizontalIcon,
  RefreshCcw,
  Plus
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import styles from './GatePass.module.css';
// <-- adjust path

import TableSkeleton from 'utils/TableSkeleton'; // check if this is default export!
import Cookies from 'js-cookie';
import GatePassRequestTable from './components/GatePassRequestTable';
import ViewPassRequest from './components/ViewPassRequest';
import GPExtensionModal from './components/GPExtensionModal';

const data = [
  { id: 1, head: 'Total Requests', icon: Users2, color: '#2563EB' },
  { id: 2, head: 'Pending', icon: GraduationCap, color: '#0284C7' },
  { id: 3, head: 'Approved', icon: Users2, color: '#9333EA' },
  { id: 4, head: 'Rejected', icon: Layers, color: '#EA580C' }
];

const GatePass = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [requestData, setRequestData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [kpiData, setKpiData] = useState(null);
  const [requestTypeTerm, setRequestTypeTerm] = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // DatePicker and Update Gate Pass state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isUpdatingGatePass, setIsUpdatingGatePass] = useState(false);
  const [showGPExtensionModal, setShowGPExtensionModal] = useState(false);
  const datePickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsPickerOpen(false);
      }
    };

    if (isPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPickerOpen]);

  const formattedSelectedDate = selectedDate
    ? `${String(selectedDate.getDate()).padStart(2, '0')}/${String(selectedDate.getMonth() + 1).padStart(2, '0')}/${selectedDate.getFullYear()}`
    : '';

  const handleUpdateGatePass = async () => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

    const token = Cookies.get('Token');
    if (!token) {
      toast.error('Authentication token missing');
      return;
    }

    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;

    try {
      setIsUpdatingGatePass(true);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/gate_pass/backdate-gatepass`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          startDate: formattedDate,
          endDate: formattedDate
        })
      });

      const result = await response.json();

      if (response.ok && (result.success !== false)) {
        const count = result?.data?.updated ?? 0;
        toast.success(`${count} gate pass${count === 1 ? '' : 'es'} updated successfully` );
        refreshTable();
      } else {
        toast.error(result?.message || 'Failed to update gate pass');
      }
    } catch (error) {
      console.error('Update gate pass error:', error);
      toast.error('Failed to update gate pass');
    } finally {
      setIsUpdatingGatePass(false);
    }
  };

  const [filters, setFilters] = useState({
    search: '',
    requestType: '',
    studentId: '',
    status: {
      Pending: false,
      Approved: false,
      Rejected: false
    }
  });
  const refreshTable = () => {
    fetchRequests(filters, page, rowsPerPage);
    fetchKpiData();
  };

  const handleStatusChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      status: {
        ...prev.status,
        [key]: value
      }
    }));
  };

  const handleReset = () => {
    const resetFilters = {
      search: '',
      requestType: '',
      studentId: '',
      status: {
        Pending: false,
        Approved: false,
        Rejected: false
      }
    };
    setSearchTerm('');
    setRequestTypeTerm('');
    setFilters(resetFilters); // update state
    setPage(0); // optional: reset page to first
    fetchRequests(resetFilters, 0, rowsPerPage); // use the reset filters
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.search === searchTerm) return; // no change, skip

      const updatedFilters = { ...filters, search: searchTerm };
      setFilters(updatedFilters);
      setPage(0);
      fetchRequests(updatedFilters, 0, rowsPerPage); // 👈 ye line missing thi
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleApply = () => {
    const updatedFilters = {
      ...filters,
      search: searchTerm, // apply searchTerm
      requestType: requestTypeTerm // apply requestTypeTerm
    };
    setFilters(updatedFilters);
    fetchRequests(updatedFilters, 0, rowsPerPage);
    setPage(0);
  };

  const handleChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const fetchRequests = async (filters = {}, pageNumber = page, limit = rowsPerPage) => {
    setLoading(true);
    try {
      // Construct query params
      const params = new URLSearchParams();
      params.append('page', pageNumber + 1);
      params.append('limit', limit);

      if (filters.search) params.append('search', filters.search);
      if (filters.requestType) params.append('requestType', filters.requestType);
      if (filters.studentId) params.append('category', filters.studentId);

      const statusArray = Object.entries(filters.status || {})
        .filter(([_, value]) => value)
        .map(([key]) => key);

      if (statusArray.length > 0) params.append('status', statusArray.join(','));

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/gate_pass/all-gatepasses?${params.toString()}`, {
        headers: { Authorization: `Bearer ${Cookies.get('Token')}` }
      });

      const result = await response.json();
      if (result.statusCode === 200) {
        const getDepartmentStatus = (approvalLogs, departmentKey) => {
          const logs = Array.isArray(approvalLogs) ? approvalLogs : [];
          const departmentLogs = logs.filter(
            (log) => String(log?.department || '').trim().toLowerCase() === departmentKey
          );

          if (departmentLogs.length === 0) return 'Not Available (N/A)';

          const latestLog = departmentLogs.reduce((latest, current) => {
            const latestTime = new Date(latest?.approvedAt || 0).getTime();
            const currentTime = new Date(current?.approvedAt || 0).getTime();
            return currentTime > latestTime ? current : latest;
          });

          return latestLog?.status || 'Not Available (N/A)';
        };

        const passes = result.data.passes.map((p) => ({
          ...p,
          _id: p._id,
          requestId: p.requestId || 'Not Available (N/A)',

          student: {
            ...p.student,
            name: [p.student?.firstName, p.student?.lastName].filter(Boolean).join(' ') || 'Not Available (N/A)',
            id: p.student?._id || p.studentId || 'Not Available (N/A)',
            UID: p.student?.UID || 'Not Available (N/A)',
            category: p.student?.category?.toUpperCase() || p.userDetails?.category || 'Not Available (N/A)',
            building_no: p.buildingDetails?.building_no || p.student?.building_no || 'Not Available (N/A)',
            building_name: p.buildingDetails?.name || 'Not Available (N/A)',
            phoneNumber: p.student?.phoneNumber || 'Not Available (N/A)'
          },

          requestType: p.requestType ? p.requestType.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Not Available (N/A)',

          // 🚀 KEEP ALL ORIGINAL DATE/TIME FIELDS FROM API
          fromDate: p.fromDate,
          toDate: p.toDate,
          fromTime: p.fromTime,
          toTime: p.toTime,

          reason: p.reason || 'Not Available (N/A)',
          status: p.status || 'Not Available (N/A)',
          academicStatus: getDepartmentStatus(p.approvalLogs, 'academic'),
          hostelStatus: getDepartmentStatus(p.approvalLogs, 'hostel'),
          submittedAt: p.createdAt || null,
          remark: p.remark || 'Not Available (N/A)',
          approvalLogs: Array.isArray(p.approvalLogs) ? p.approvalLogs : [],

          approvedBy: p.approvedBy
            ? `${p.approvedBy.firstName || ''} ${p.approvedBy.lastName || ''}`.trim() || 'Not Available (N/A)'
            : 'Not Available (N/A)',
          approvedByObject: p.approvedBy || null,
          rejectedBy: p.rejectedBy
            ? `${p.rejectedBy.firstName || ''} ${p.rejectedBy.lastName || ''}`.trim() || 'Not Available (N/A)'
            : 'Not Available (N/A)',
          rejectedByObject: p.rejectedBy || null,
          approvedAt: p.approvedAt || null,
          rejectedAt: p.rejectedAt || null,
          updatedAt: p.updatedAt || null
        }));

        setRequestData(passes);
        setTotalCount(result.data.total);
      } else {
        toast.error(result.message || 'Failed to fetch gate pass requests');
      }
    } catch (error) {
      console.error('Failed to fetch requests:', error);
      toast.error('Failed to fetch gate pass requests');
    } finally {
      setLoading(false);
    }
  };
  const fetchKpiData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/gate_pass/gatepasses/counts`, {
        headers: {
          Authorization: `Bearer ${Cookies.get('Token')}`
        }
      });
      const result = await response.json();
      if (result.statusCode === 200) {
        setKpiData(result.data);
        setPendingCount(result.data.pending || 0);
      } else {
        toast.error(result.message || 'Failed to fetch KPI data');
      }
    } catch (error) {
      console.error('Failed to fetch KPI data:', error);
      toast.error('Failed to fetch KPI data');
    }
  };

  useEffect(() => {
    fetchKpiData();
  }, []);
  useEffect(() => {
    fetchRequests(filters, page, rowsPerPage);
  }, [page, rowsPerPage]);

  // Filter requests
  const filteredRequests = requestData.filter((r) => {
    const term = filters.search.trim().toLowerCase(); // use filters.search
    if (!term) return true;
    return (
      r.requestId?.toLowerCase().includes(term) ||
      r.student?.name?.toLowerCase().includes(term) ||
      r.student?.id?.toLowerCase().includes(term)
    );
  });

  const currentPageData = filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Status chip colors
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return { backgroundColor: '#FACC15', color: '#78350F' };
      case 'approved':
        return { backgroundColor: '#16A34A', color: '#FFFFFF' };
      case 'rejected':
        return { backgroundColor: '#DC2626', color: '#FFFFFF' };
      case 'in progress':
        return { backgroundColor: '#2563EB', color: '#FFFFFF' };
      default:
        return { backgroundColor: '#E5E7EB', color: '#111827' };
    }
  };

  // // Menu handlers
  // const handleMenuOpen = (event, request) => {
  //   setAnchorEl(event.currentTarget);
  //   setSelectedRequest(request);
  // };
  // const handleMenuClose = () => {
  //   setAnchorEl(null);
  //   setSelectedRequest(null);
  // };

  return (
    <Box className={styles.container}>
      {/* Header */}
      <Box className={styles.topHeader}>
        <Box className={styles.headerLeft}>
          <Typography className={styles.mainTitle}>Gate Pass Management</Typography>
          <Typography className={styles.subtitle}>View gate pass requests</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <div className={styles.datePickerContainer} ref={datePickerRef}>
            <button
              className={styles.dateButton}
              type="button"
              aria-expanded={isPickerOpen}
              onClick={() => setIsPickerOpen((prev) => !prev)}
            >
              <span style={{ color: '#475569' }}>{formattedSelectedDate}</span>
              <span className={styles.calendarIcon} aria-hidden>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M6 1.49976V3.74976"
                    stroke="#A0A0A0"
                    strokeWidth="1.125"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 1.49976V3.74976"
                    stroke="#A0A0A0"
                    strokeWidth="1.125"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2.625 6.81738H15.375"
                    stroke="#A0A0A0"
                    strokeWidth="1.125"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15.75 6.37476V12.7498C15.75 14.9998 14.625 16.4998 12 16.4998H6C3.375 16.4998 2.25 14.9998 2.25 12.7498V6.37476C2.25 4.12476 3.375 2.62476 6 2.62476H12C14.625 2.62476 15.75 4.12476 15.75 6.37476Z"
                    stroke="#A0A0A0"
                    strokeWidth="1.125"
                    strokeMiterlimit="10"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path d="M11.7713 10.2749H11.778" stroke="#A0A0A0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M11.7713 12.5249H11.778" stroke="#A0A0A0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M8.99588 10.2749H9.00262" stroke="#A0A0A0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M8.99588 12.5249H9.00262" stroke="#A0A0A0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6.22049 10.2749H6.22723" stroke="#A0A0A0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6.22049 12.5249H6.22723" stroke="#A0A0A0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </button>
            {isPickerOpen && (
              <div className={styles.datePickerPopover}>
                <DatePicker
                  inline
                  selected={selectedDate}
                  maxDate={new Date()}
                  calendarClassName={styles.customCalendar}
                  onChange={(date) => {
                    if (date) {
                      setSelectedDate(date);
                    }
                    setIsPickerOpen(false);
                  }}
                />
              </div>
            )}
          </div>

          <Button
            variant="contained"
            className={styles.updateGatePassBtn}
            onClick={handleUpdateGatePass}
            disabled={isUpdatingGatePass}
            startIcon={
              <RefreshCw
                size={18}
                className={isUpdatingGatePass ? styles.spin : ''}
              />
            }
          >
            {isUpdatingGatePass ? 'Updating...' : 'Update Gate Pass'}
          </Button>

          <Button
            variant="contained"
            className={styles.gpExtensionBtn}
            onClick={() => setShowGPExtensionModal(true)}
            startIcon={<Plus size={18} />}
          >
            GP Extension
          </Button>
        </Box>
      </Box>

      {/* KPI Grid */}
      {/* KPI Grid */}
      <div className={styles.gridContainer}>
        {data.map((item) => {
          const Icon = item.icon;

          // map item.head to correct KPI value
          let value = 0;
          if (kpiData) {
            if (item.head === 'Pending') value = kpiData.pending;
            else if (item.head === 'Approved') value = kpiData.approved;
            else if (item.head === 'Rejected') value = kpiData.rejected;
            else if (item.head === 'Total Requests') {
              value = kpiData.total;
            }
          }

          return (
            <div key={item.id} className={styles.gridItem}>
              <div className={styles.itemHead}>
                <p>{item.head}</p>
                <Icon width={24} color={item.color} height={24} />
              </div>
              <div>
                <h2>{value}</h2>
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.filtersSection}>
        <div className={styles.filterHeading}>
          <h5>Filters</h5>
          <SlidersHorizontalIcon height={24} width={24} />
        </div>

        <div className={styles.inputSection}>
          {/* Search */}
          <div>
            <TextField
              size="small"
              label="Search"
              placeholder="Search by name or ID"
              className={styles.searchInput}
              value={searchTerm} // input bound to searchTerm
              onChange={(e) => setSearchTerm(e.target.value)} // updates local input only
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          </div>

          {/* Request Type */}
          <div>
            <FormControl size="small" className={styles.selectInput}>
              <InputLabel shrink id="requestType-label">
                Request Type
              </InputLabel>
              <Select
                labelId="requestType-label"
                value={requestTypeTerm} // bind to local state
                onChange={(e) => setRequestTypeTerm(e.target.value)} // update local only
                label="Request Type"
                displayEmpty
                renderValue={(selected) => {
                  if (selected === '') {
                    return 'None';
                  }

                  const labelMap = {
                    leave: 'Leave',
                    late_entry: 'Late Entry',
                    early_departure: 'Early Departure'
                  };

                  return labelMap[selected];
                }}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="leave">Leave</MenuItem>
                <MenuItem value="late_entry">Late Entry</MenuItem>
                <MenuItem value="early_departure">Early Departure</MenuItem>
              </Select>
            </FormControl>
          </div>

          {/* Student Selector */}
          <div>
            <FormControl size="small" className={styles.selectInput}>
              <InputLabel shrink id="student-label">Category</InputLabel>
              <Select
                labelId="student-label"
                value={filters.studentId}
                onChange={(e) => handleChange('studentId', e.target.value)}
                label="Student"
                displayEmpty
                renderValue={(selected) => {
                  if (selected === '') {
                    return 'None';
                  }

                  

                  return selected.toUpperCase();
                }}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="ug">UG</MenuItem>
                <MenuItem value="pg">PG</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>

        {/* Status Checkboxes */}
        <div className={styles.statusRowParent}>
          <div className={styles.statusRow}>
            <h5>Status</h5>
            <Box className={styles.statusOptions}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={filters.status.Pending}
                    onChange={(e) => handleStatusChange('Pending', e.target.checked)}
                  />
                }
                label="Pending"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={filters.status.Approved}
                    onChange={(e) => handleStatusChange('Approved', e.target.checked)}
                  />
                }
                label="Approved"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={filters.status.Rejected}
                    onChange={(e) => handleStatusChange('Rejected', e.target.checked)}
                  />
                }
                label="Rejected"
              />
            </Box>
          </div>

          <div className={styles.actionsRow}>
            <button type="button" className={styles.resetBtn} onClick={handleReset}>
              <RefreshCcw width={16} height={16} />
              Reset
            </button>
            <button type="button" className={styles.applyBtn} onClick={handleApply} disabled={loading}>
              <Check size={16} height={16} width={16} color="#FFF" /> {loading ? 'Loading...' : 'Apply'}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.complaintListTable}>
        <GatePassRequestTable
          pendingCount={pendingCount}
          data={requestData}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(newPage) => setPage(newPage)}
          onRowsPerPageChange={(newRows) => {
            setRowsPerPage(newRows);
            setPage(0);
            fetchRequests(filters, 0, newRows);
          }}
          refreshTable={refreshTable}
          totalCount={totalCount}
        />
      </div>

      {/* GP Extension Modal */}
      <GPExtensionModal
        open={showGPExtensionModal}
        onClose={() => setShowGPExtensionModal(false)}
        onRefresh={refreshTable}
      />

      <ToastContainer
        enableMultiContainer
        containerId="uploadResults"
        className="uploadToastContainer"
        hideProgressBar
      />
    </Box>
  );
};

export default GatePass;
